import React, { useState, useEffect, useRef, useMemo } from 'react';
import { KioskHeader, formatPhone, formatPrice, KioskKeypad } from './KioskFlow';
import useIdleTimeout, { IdleWarningModal } from './useIdleTimeout';
import { trackAddToCart, trackBeginCheckout, trackInStoreBooking } from './analytics';
import { setKioskContext, kev } from './kioskAnalytics';
import GuidedAddOnsFlow from './booking/GuidedAddOnsFlow';
import GuidedRemovalDecisionScreen from './booking/GuidedRemovalDecisionScreen';
import GuidedRemovalTypeScreen from './booking/GuidedRemovalTypeScreen';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Idle timeout per step (seconds)
const WALKIN_TIMEOUTS = {
  identify: 10,       // auto-reset
  phone: 10,          // auto-reset
  name: 30,
  signup: 120,
  anatomy: 30,
  services: 120,
  addons: 90,
  removal: 60,
  removal_select: 60,
  available: 90,
  confirm: 60,
  success: 45,        // auto-reset (enough time for check-in retries)
};

// ============================================================
// WALK-IN QUICK BOOK — simplified same-day booking
// Identify → Anatomy → Services → Add-Ons → Available → Confirm → Done
// ============================================================
export default function KioskWalkIn({ onBack, onBookAnotherDay, centerId, initialGuest, initialPhone, employee, terminal }) {
  const isDemo = new URLSearchParams(window.location.search).get('demo') === 'walkin';
  const [step, setStep] = useState(isDemo ? 'success' : (initialGuest ? 'anatomy' : 'identify'));

  // Guest identification
  const [phone, setPhone] = useState(initialPhone || '');
  const [guest, setGuest] = useState(initialGuest || null);
  const [isNewGuest, setIsNewGuest] = useState(false);
  const [multipleGuests, setMultipleGuests] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState(null);

  // New guest sign-up fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [howHeard, setHowHeard] = useState('');
  const [signupField, setSignupField] = useState('firstName'); // tracks active field for keypad

  // Anatomy filter
  const [anatomyFilter, setAnatomyFilter] = useState(null); // 'V' or 'P'

  // Service selection
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryServices, setCategoryServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [fullCatalog, setFullCatalog] = useState(null);
  const catalogRef = useRef(null);
  const catalogPromiseRef = useRef(null);
  const [serviceSearch, setServiceSearch] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState(null); // null = show all

  // Add-ons
  const [serviceAddOns, setServiceAddOns] = useState({}); // {serviceId: {optional: [], mandatory: []}}
  const [selectedOptionalAddOns, setSelectedOptionalAddOns] = useState([]);
  const [loadingAddOns, setLoadingAddOns] = useState(false);

  // Mandatory add-ons (removal)
  const [mandatoryServices, setMandatoryServices] = useState([]); // services that have mandatory add-ons
  const [currentMandatoryIdx, setCurrentMandatoryIdx] = useState(0);
  const [selectedMandatoryAddOns, setSelectedMandatoryAddOns] = useState([]);

  // Available slots
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(isDemo ? { time: '2:30 PM', therapist: { name: 'Vlada' }, centerTime: new Date().toISOString() } : null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [therapistFilter, setTherapistFilter] = useState(null);

  // Confirmed
  const [bookingConfirmed, setBookingConfirmed] = useState(isDemo);
  const [bookedAppointmentGroupId, setBookedAppointmentGroupId] = useState(isDemo ? 'demo-group' : null);
  const [bookedAppointmentId, setBookedAppointmentId] = useState(null);
  const [checkedIn, setCheckedIn] = useState(isDemo);

  // Idle timeout — pause while pageLoading (API in progress, user is waiting not idle)
  const isAutoReset = step === 'identify' || step === 'phone' || step === 'name' || step === 'success';
  const { showWarning, countdown, dismissWarning } = useIdleTimeout({
    timeoutSeconds: (pageLoading || loading) ? 0 : (WALKIN_TIMEOUTS[step] || 0),
    onReset: () => { kev(step, 'idle_timeout', { screen: step }); onBack(); },
    autoReset: isAutoReset,
  });

  // Compute nail and wax service IDs for guided add-on flow
  const nailServiceIds = useMemo(() => {
    if (!fullCatalog || !categories.length) return new Set();
    const nailCatIds = categories.filter(c => (c.name || '').toLowerCase().includes('nail')).map(c => c.id);
    const ids = new Set();
    for (const catId of nailCatIds) {
      for (const svc of (fullCatalog.services[catId] || [])) {
        ids.add(svc.id);
      }
    }
    return ids;
  }, [fullCatalog, categories]);

  const waxServiceIds = useMemo(() => {
    if (!fullCatalog || !categories.length) return new Set();
    const waxCatIds = categories.filter(c => (c.name || '').toLowerCase().includes('wax')).map(c => c.id);
    const ids = new Set();
    for (const catId of waxCatIds) {
      for (const svc of (fullCatalog.services[catId] || [])) {
        ids.add(svc.id);
      }
    }
    return ids;
  }, [fullCatalog, categories]);

  // If guest was passed from another flow (e.g. check-in → book), load catalog immediately
  useEffect(() => {
    if (initialGuest) {
      loadCatalog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================================
  // GUEST LOOKUP
  // ============================================================
  async function lookupGuest(overrideFirstName) {
    setLoading(true);
    setPageLoading(true);
    setError(null);
    try {
      const url = overrideFirstName
        ? `${API_URL}/api/kiosk/guest-lookup?phone=${phone}&centerId=${centerId}&firstName=${encodeURIComponent(overrideFirstName)}`
        : `${API_URL}/api/kiosk/guest-lookup?phone=${phone}&centerId=${centerId}`;
      const res = await fetch(url);
      const data = await res.json();

      if (data.multipleMatches && !overrideFirstName) {
        setMultipleGuests(true);
        setStep('name');
        return;
      }

      if (!data.found) { kev('phone', 'guest_not_found'); throw new Error('We couldn\'t find your account. Try again or sign up as new.'); }
      setGuest(data.guest);
      setKioskContext({ guestId: data.guest?.guestId });
      kev('phone', 'guest_found', { isReturning: true });
      await loadCatalog();
      setStep('anatomy');
    } catch (err) {
      setError(err.message || 'Could not look up your account.');
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  }

  // ============================================================
  // NEW GUEST SIGN-UP
  // ============================================================
  async function signUpGuest() {
    setLoading(true);
    setPageLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/kiosk/guest-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName, lastName, phone, email,
          dateOfBirth: dob,
          gender: gender ? parseInt(gender) : -1,
          referralCode,
          referralSource: howHeard,
          centerId
        })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setGuest(data.guest);
      setKioskContext({ guestId: data.guest?.guestId });
      kev('signup', 'guest_identified', { isNew: true, hasReferral: !!referralCode });
      await loadCatalog();
      setStep('anatomy');
    } catch (err) {
      setError(err.message || 'Could not create your account.');
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  }

  // ============================================================
  // ANATOMY SELECTION
  // ============================================================
  function handleAnatomySelect(filter) {
    setAnatomyFilter(filter);
    kev('anatomy', 'anatomy_selected', { filter });
    setStep('services');
  }

  // ============================================================
  // LOAD SERVICE CATALOG
  // ============================================================
  async function loadCatalog() {
    if (catalogRef.current) return catalogRef.current;
    if (catalogPromiseRef.current) return catalogPromiseRef.current;
    setLoadingServices(true);
    const promise = (async () => {
      try {
        const res = await fetch(`${API_URL}/api/kiosk/centers/${centerId}/full-catalog`);
        const data = await res.json();

        // Transform services from cache format to walk-in format (flat price, preserve code for anatomy)
        const transformedServices = {};
        for (const [catId, svcs] of Object.entries(data.services || {})) {
          transformedServices[catId] = (svcs || [])
            .filter(s => !s.add_ons_info?.is_add_on && s.duration > 0)
            .map(s => ({
              id: s.id,
              name: s.display_name || s.name,
              code: s.code || '',
              price: s.price_info?.sale_price || 0,
              duration: s.duration || 0,
              subCategory: s.sub_category_name || '',
              displayOrder: s.display_order ?? 9999999,
              description: s.html_description || ''
            }));
        }
        data.services = transformedServices;

        catalogRef.current = data;
        setFullCatalog(data);
        setCategories(data.categories || []);
        if (data.categories?.length > 0) setSelectedCategory(data.categories[0].id);
        return data;
      } catch (err) {
        console.error('Failed to load catalog', err);
        return null;
      } finally {
        setLoadingServices(false);
        catalogPromiseRef.current = null;
      }
    })();
    catalogPromiseRef.current = promise;
    return promise;
  }

  // Filter services by anatomy code prefix and search query
  function getFilteredServices() {
    let services = serviceSearch
      ? Object.values(fullCatalog?.services || {}).flat() // search across ALL categories
      : categoryServices;

    if (anatomyFilter) {
      services = services.filter(s => {
        const code = (s.code || '').toUpperCase();
        if (code.startsWith('V-')) return anatomyFilter === 'V';
        if (code.startsWith('P-')) return anatomyFilter === 'P';
        return true; // No prefix = unisex
      });
    }

    if (serviceSearch) {
      const q = serviceSearch.toLowerCase();
      services = services.filter(s =>
        s.name.toLowerCase().includes(q) ||
        (s.subCategory || '').toLowerCase().includes(q)
      );
    }

    return services;
  }

  // Update displayed services when category changes
  useEffect(() => {
    if (selectedCategory && fullCatalog) {
      const raw = fullCatalog.services[selectedCategory] || [];
      setCategoryServices(raw);
      setSelectedSubCategory(null); // reset sub-category filter
    }
  }, [selectedCategory, fullCatalog]);

  function toggleService(svc) {
    setSelectedServices(prev => {
      const exists = prev.find(s => s.id === svc.id);
      if (exists) return prev.filter(s => s.id !== svc.id);
      trackAddToCart(svc.name, svc.price, 'Walk-In');
      return [...prev, svc];
    });
  }

  function getTotalDuration() {
    const svcDur = selectedServices.reduce((sum, s) => sum + s.duration, 0);
    const optDur = selectedOptionalAddOns.reduce((sum, a) => sum + (a.duration || 0), 0);
    const manDur = selectedMandatoryAddOns.reduce((sum, a) => sum + (a.duration || 0), 0);
    return svcDur + optDur + manDur;
  }

  function getTotalPrice() {
    const svcPrice = selectedServices.reduce((sum, s) => sum + s.price, 0);
    const optPrice = selectedOptionalAddOns.reduce((sum, a) => sum + (a.price || 0), 0);
    const manPrice = selectedMandatoryAddOns.reduce((sum, a) => sum + (a.price || 0), 0);
    return svcPrice + optPrice + manPrice;
  }

  // ============================================================
  // ADD-ONS LOGIC
  // ============================================================
  async function loadAddOnsForServices() {
    setLoadingAddOns(true);
    try {
      const catalog = catalogRef.current || await loadCatalog();
      const filterInternal = (list) => (list || []).filter(a => !/^Z\s/i.test(a.name || ''));
      const newAddOns = {};
      for (const svc of selectedServices) {
        const raw = catalog?.addOns?.[svc.id] || { optional: [], mandatory: [] };
        newAddOns[svc.id] = { optional: filterInternal(raw.optional), mandatory: filterInternal(raw.mandatory) };
      }
      setServiceAddOns(newAddOns);

      const withMandatory = selectedServices.filter(svc => (newAddOns[svc.id]?.mandatory || []).length > 0);
      setMandatoryServices(withMandatory);
      return newAddOns;
    } catch (err) {
      console.error('Failed to load add-ons', err);
      return {};
    } finally {
      setLoadingAddOns(false);
    }
  }

  async function proceedFromServices() {
    kev('services', 'services_selected', { count: selectedServices.length, services: selectedServices.map(s => s.name) });
    setPageLoading(true);
    try {
      const addOns = await loadAddOnsForServices();

      // Check if any selected services have optional add-ons (guided flow handles all types via sub-category)
      const hasOptional = selectedServices.some(svc => {
        const a = addOns[svc.id];
        return a && a.optional && a.optional.length > 0;
      });

      // Check if any have mandatory (non-guided fallback)
      const withMandatory = selectedServices.filter(svc => (addOns[svc.id]?.mandatory || []).length > 0);

      if (hasOptional) {
        setStep('addons');
      } else if (withMandatory.length > 0) {
        setCurrentMandatoryIdx(0);
        setStep('removal');
      } else {
        await loadAvailableSlots();
        setStep('available');
      }
    } finally { setPageLoading(false); }
  }

  function toggleOptionalAddOn(addOn, parentServiceId) {
    setSelectedOptionalAddOns(prev => {
      const exists = prev.find(a => a.id === addOn.id && a.parentServiceId === parentServiceId);
      if (exists) return prev.filter(a => !(a.id === addOn.id && a.parentServiceId === parentServiceId));
      trackAddToCart(addOn.name, addOn.price, 'Walk-In Add-On');
      return [...prev, { ...addOn, parentServiceId }];
    });
  }

  async function proceedFromOptionalAddOns() {
    setPageLoading(true);
    try {
      if (mandatoryServices.length > 0) {
        setCurrentMandatoryIdx(0);
        setStep('removal');
      } else {
        await loadAvailableSlots();
        setStep('available');
      }
    } finally { setPageLoading(false); }
  }

  function handleRemovalAnswer(serviceId, answer) {
    if (answer && serviceAddOns[serviceId]?.mandatory?.length > 0) {
      setStep('removal_select');
    } else {
      advanceMandatory();
    }
  }

  function selectMandatoryAddOn(addOn, parentServiceId) {
    setSelectedMandatoryAddOns(prev => {
      const filtered = prev.filter(a => a.parentServiceId !== parentServiceId);
      return [...filtered, { ...addOn, parentServiceId, isMandatory: true }];
    });
  }


  async function advanceMandatory() {
    setPageLoading(true);
    try {
      const nextIdx = currentMandatoryIdx + 1;
      if (nextIdx < mandatoryServices.length) {
        setCurrentMandatoryIdx(nextIdx);
        setStep('removal');
      } else {
        await loadAvailableSlots();
        setStep('available');
      }
    } finally { setPageLoading(false); }
  }

  // ============================================================
  // LOAD AVAILABLE SLOTS (today only, next 2 hours)
  // ============================================================
  async function loadAvailableSlots() {
    setLoadingSlots(true);
    setTherapistFilter(null);
    try {
      const n = new Date();
      const today = n.getFullYear() + '-' + String(n.getMonth() + 1).padStart(2, '0') + '-' + String(n.getDate()).padStart(2, '0');
      const allServices = [
        ...selectedServices.map(s => ({ serviceId: s.id, serviceName: s.name, duration: s.duration, therapistId: null })),
        ...selectedOptionalAddOns.map(a => ({ serviceId: a.id, serviceName: a.name, duration: a.duration, isAddOn: true, parentServiceId: a.parentServiceId })),
        ...selectedMandatoryAddOns.map(a => ({ serviceId: a.id, serviceName: a.name, duration: a.duration, isAddOn: true, parentServiceId: a.parentServiceId })),
      ];
      const res = await fetch(`${API_URL}/api/availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ centerId, date: today, services: allServices, checkFutureDays: false })
      });
      const data = await res.json();

      const now = Date.now();
      const openSlots = data.openSlots || [];
      const slots = openSlots.map(slot => {
        const therapist = slot.SlotBookings?.[0]?.Services?.[0]?.Therapist;
        const timeStr = slot.Time; // "2026-02-17T14:00:00" or "2026-02-17 14:00:00"
        const timePart = timeStr.includes('T') ? timeStr.split('T')[1] : timeStr.split(' ')[1];
        const [h, m] = timePart.split(':').map(Number);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
        const displayTime = `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
        return {
          therapist: {
            id: therapist?.Id || 'unknown',
            name: therapist?.Name || therapist?.DisplayName || 'Available',
            title: ''
          },
          time: displayTime,
          timeValue: new Date(timeStr.replace(' ', 'T')).getTime(),
          centerTime: timeStr
        };
      }).filter(slot => slot.timeValue > now);

      slots.sort((a, b) => a.timeValue - b.timeValue);
      setAvailableSlots(slots);
    } catch (err) {
      console.error('Failed to load slots', err);
    } finally {
      setLoadingSlots(false);
    }
  }

  // ============================================================
  // CONFIRM BOOKING
  // ============================================================
  async function confirmBooking() {
    setLoading(true);
    setPageLoading(true);
    setError(null);
    trackBeginCheckout(
      selectedServices.map(s => ({ name: s.name, price: s.price })),
      getTotalPrice()
    );
    try {
      const allServices = [
        ...selectedServices.map(s => ({ serviceId: s.id, serviceName: s.name, duration: s.duration, therapistId: therapistFilter ? selectedSlot?.therapist?.id : 'any' })),
        ...selectedOptionalAddOns.map(a => ({ serviceId: a.id, serviceName: a.name, duration: a.duration, isAddOn: true, parentServiceId: a.parentServiceId })),
        ...selectedMandatoryAddOns.map(a => ({ serviceId: a.id, serviceName: a.name, duration: a.duration, isAddOn: true, parentServiceId: a.parentServiceId, isMandatory: true })),
      ];

      // Use V1 kiosk rebook endpoint (admin-level, no guest auth needed)
      const rebookRes = await fetch(`${API_URL}/api/kiosk/rebook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          centerId,
          guestId: guest?.guestId,
          services: allServices,
          slotTime: selectedSlot?.centerTime
        })
      });
      const rebookData = await rebookRes.json();
      if (rebookData.error) throw new Error(rebookData.error);

      trackInStoreBooking(selectedServices, getTotalPrice(), selectedSlot?.therapist?.name);
      kev('confirm', 'booking_confirmed', {
        services: selectedServices.map(s => s.name),
        serviceCount: selectedServices.length,
        therapist: selectedSlot?.therapist?.name,
        isNew: !!(guest && !guest.visitCount),
        total: getTotalPrice(),
      });
      kev('success', 'flow_complete');
      setBookedAppointmentGroupId(rebookData.appointmentGroupId || null);
      setBookedAppointmentId(rebookData.appointmentId || null);
      setBookingConfirmed(true);
      setStep('success');
    } catch (err) {
      console.error('Booking error:', err);
      setError(err.message || 'Failed to book. Please ask the front desk for help.');
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  }

  // ============================================================
  // CHECK IN NOW (after same-day booking) — button-triggered
  // ============================================================
  async function checkInNow() {
    setLoading(true);
    setError(null);
    const id = bookedAppointmentId || bookedAppointmentGroupId;
    const groupId = bookedAppointmentGroupId || bookedAppointmentId;
    console.log(`[KIOSK] Walk-in check-in: id=${id}, groupId=${groupId}`);

    // Retry — Zenoti needs time to finalize freshly booked appointments
    // First attempt after 5s, then 8s between retries
    const maxAttempts = 4;
    const delays = [5000, 8000, 8000, 8000];
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        await new Promise(r => setTimeout(r, delays[attempt - 1]));

        const res = await fetch(`${API_URL}/api/kiosk/checkin`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            appointmentId: id,
            appointmentGroupId: groupId,
            centerId,
            guestId: guest?.guestId
          })
        });
        const data = await res.json();
        if (res.ok && !data.error) {
          console.log(`[KIOSK] Walk-in check-in succeeded on attempt ${attempt}`);
          setCheckedIn(true);
          setLoading(false);
          return;
        }
        console.log(`[KIOSK] Walk-in check-in attempt ${attempt}/${maxAttempts} failed:`, data.error || res.status);
      } catch (err) {
        console.log(`[KIOSK] Walk-in check-in attempt ${attempt}/${maxAttempts} error:`, err.message);
      }
    }
    console.error('[KIOSK] Walk-in check-in failed after all attempts');
    setError('Could not check in automatically. Please ask the front desk.');
    setLoading(false);
  }

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="kiosk-container">
      <KioskHeader />

      {/* Full-screen loading overlay for page transitions */}
      {pageLoading && (
        <div className="kiosk-page-loading-overlay">
          <div className="kiosk-page-spinner" />
        </div>
      )}

      <div className={`kiosk-main ${step === 'services' ? 'kiosk-main-wide' : ''}`}>

        {/* IDENTIFY — Been here before? */}
        {step === 'identify' && (
          <div className="kiosk-screen kiosk-walkin-identify kiosk-fade-in">
            <div className="kiosk-screen-title" style={{ fontSize: 28 }}>Have you been here before?</div>
            <div className="kiosk-walkin-id-buttons kiosk-slide-up kiosk-delay-1">
              <button className="kiosk-walkin-id-btn" onClick={() => setStep('phone')}>
                <div className="kiosk-walkin-id-icon">👋</div>
                <div className="kiosk-walkin-id-label">Yes</div>
                <div className="kiosk-walkin-id-sub">I have an account</div>
              </button>
              <button className="kiosk-walkin-id-btn" onClick={() => { setIsNewGuest(true); setStep('signup'); }}>
                <div className="kiosk-walkin-id-icon">✨</div>
                <div className="kiosk-walkin-id-label">I'm New</div>
                <div className="kiosk-walkin-id-sub">First time here</div>
              </button>
            </div>
            <button className="kiosk-nav-back" style={{ marginTop: 24 }} onClick={onBack}>← Back</button>
          </div>
        )}

        {/* PHONE — returning customer */}
        {step === 'phone' && (
          <div className="kiosk-screen kiosk-phone-screen kiosk-fade-in" style={{ flex: 'none' }}>
            <div className="kiosk-screen-title">Enter your phone number</div>
            <div className="kiosk-screen-sub">We'll pull up your account</div>
            <input
              className="kiosk-phone-input"
              type="tel"
              placeholder="(604) 555-1234"
              value={formatPhone(phone)}
              readOnly
            />
            <KioskKeypad mode="numeric" value={phone} onChange={v => setPhone(v.slice(0, 10))} maxLength={10} />
            {error && <div style={{ color: '#c44', fontSize: 13, textAlign: 'center' }}>{error}</div>}
            <button
              className="kiosk-btn-primary"
              disabled={phone.length < 10 || loading}
              onClick={() => lookupGuest()}
            >
              {loading ? 'Looking up...' : 'Find Me'}
            </button>
            <button className="kiosk-nav-back" onClick={() => { setError(null); setStep('identify'); }}>← Back</button>
          </div>
        )}

        {/* FIRST NAME — multiple accounts on this phone */}
        {step === 'name' && (
          <div className="kiosk-screen kiosk-phone-screen kiosk-fade-in">
            <div className="kiosk-screen-title">What's your first name?</div>
            <div className="kiosk-screen-sub">We found more than one account with this number</div>
            <input
              className="kiosk-phone-input"
              type="text"
              placeholder="First name"
              value={nameInput}
              readOnly
            />
            <KioskKeypad mode="alpha" value={nameInput} onChange={setNameInput} maxLength={30} />
            {error && <div style={{ color: '#c44', fontSize: 13, textAlign: 'center' }}>{error}</div>}
            <button
              className="kiosk-btn-primary"
              disabled={!nameInput.trim() || loading}
              onClick={() => lookupGuest(nameInput.trim())}
            >
              {loading ? 'Looking up...' : 'Continue'}
            </button>
            <button className="kiosk-nav-back" onClick={() => { setError(null); setNameInput(''); setStep('phone'); }}>← Back</button>
          </div>
        )}

        {/* SIGN UP — new customer */}
        {step === 'signup' && (() => {
          const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
          const showEmailError = signupField === 'email' && email.length > 0 && !emailValid;
          const signupFields = {
            firstName:    { value: firstName, set: setFirstName, mode: 'alpha', label: 'First Name *', maxLen: 30 },
            lastName:     { value: lastName, set: setLastName, mode: 'alpha', label: 'Last Name *', maxLen: 30 },
            phone:        { value: phone, set: v => setPhone(v.slice(0, 10)), mode: 'numeric', label: 'Phone *', maxLen: 10 },
            email:        { value: email, set: setEmail, mode: 'email', label: 'Email *', maxLen: 60 },
          };
          const active = signupFields[signupField] || signupFields.firstName;
          return (
          <div className="kiosk-screen kiosk-signup-screen kiosk-fade-in">
            <div className="kiosk-screen-title" style={{ fontSize: 24 }}>Welcome! Let's get you set up</div>
            <div className="kiosk-signup-form kiosk-slide-up kiosk-delay-1">
              <div className="kiosk-signup-row">
                <input className={`kiosk-signup-input ${signupField === 'firstName' ? 'kiosk-field-active' : ''}`}
                  type="text" placeholder="First Name *" value={firstName} readOnly
                  onClick={() => setSignupField('firstName')} />
                <input className={`kiosk-signup-input ${signupField === 'lastName' ? 'kiosk-field-active' : ''}`}
                  type="text" placeholder="Last Name *" value={lastName} readOnly
                  onClick={() => setSignupField('lastName')} />
              </div>
              <input className={`kiosk-signup-input ${signupField === 'phone' ? 'kiosk-field-active' : ''}`}
                type="tel" placeholder="Phone — (604) 555-1234"
                value={formatPhone(phone)} readOnly
                onClick={() => setSignupField('phone')} />
              <input className={`kiosk-signup-input ${signupField === 'email' ? 'kiosk-field-active' : ''} ${showEmailError ? 'kiosk-field-error' : ''}`}
                type="email" placeholder="Email *" value={email} readOnly
                onClick={() => setSignupField('email')} />
              {showEmailError && (
                <div style={{ color: '#c44', fontSize: 12, marginTop: -8, marginBottom: 4 }}>
                  Please enter a valid email like name@example.com
                </div>
              )}
              <div className="kiosk-signup-row">
                <div className="kiosk-signup-field">
                  <label className="kiosk-signup-label">Date of Birth *</label>
                  <input className="kiosk-signup-input" type="date" value={dob} onChange={e => setDob(e.target.value)} />
                </div>
                <div className="kiosk-signup-field">
                  <label className="kiosk-signup-label">Gender</label>
                  <select className="kiosk-signup-input" value={gender} onChange={e => setGender(e.target.value)}>
                    <option value="">Prefer not to say</option>
                    <option value="1">Female</option>
                    <option value="0">Male</option>
                    <option value="2">Non-binary</option>
                  </select>
                </div>
              </div>
              <div className="kiosk-signup-field">
                <label className="kiosk-signup-label">How did you hear about us?</label>
                <select className="kiosk-signup-input" value={howHeard} onChange={e => setHowHeard(e.target.value)}>
                  <option value="">Select...</option>
                  <option value="Advertisement">Advertisement</option>
                  <option value="Chat Gpt">Chat GPT</option>
                  <option value="Facebook">Facebook</option>
                  <option value="Friend Referral">Friend Referral</option>
                  <option value="Instagram">Instagram</option>
                  <option value="Internet Search">Internet Search</option>
                  <option value="Walk-in">Walk-in</option>
                  <option value="Word of Mouth">Word of Mouth</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            {/* Shared keypad for active field */}
            <div style={{ fontSize: 12, color: '#999', fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Typing into: {active.label}
            </div>
            <KioskKeypad
              mode={active.mode}
              value={active.value}
              onChange={active.set}
              maxLength={active.maxLen}
            />

            {error && <div style={{ color: '#c44', fontSize: 13, textAlign: 'center' }}>{error}</div>}
            <button className="kiosk-btn-primary"
              disabled={!firstName || !lastName || phone.length < 10 || !emailValid || !dob || loading}
              onClick={signUpGuest} style={{ marginTop: 8 }}>
              {loading ? 'Creating account...' : 'Continue'}
            </button>
            <button className="kiosk-nav-back" onClick={() => { setError(null); setStep('identify'); }}>← Back</button>
          </div>
          );
        })()}

        {/* ANATOMY — V or P filter */}
        {step === 'anatomy' && (
          <div className="kiosk-screen kiosk-walkin-identify kiosk-fade-in">
            <div className="kiosk-greeting">Hi {guest?.firstName}!</div>
            <div className="kiosk-screen-title" style={{ fontSize: 24 }}>Anatomy</div>
            <div className="kiosk-screen-sub" style={{ marginTop: -8 }}>We'll filter all the services available for you</div>
            <div className="kiosk-walkin-id-buttons kiosk-slide-up kiosk-delay-1">
              <button className="kiosk-walkin-id-btn kiosk-anatomy-btn" onClick={() => handleAnatomySelect('V')}>
                <div className="kiosk-anatomy-letter">V</div>
                <div className="kiosk-walkin-id-sub">For those with a vulva</div>
              </button>
              <button className="kiosk-walkin-id-btn kiosk-anatomy-btn" onClick={() => handleAnatomySelect('P')}>
                <div className="kiosk-anatomy-letter">P</div>
                <div className="kiosk-walkin-id-sub">For those with a penis</div>
              </button>
            </div>
            <button className="kiosk-nav-back" style={{ marginTop: 24 }} onClick={() => setStep('identify')}>← Back</button>
          </div>
        )}

        {/* SERVICES — pick what you want */}
        {step === 'services' && (() => {
          // Get all services (filtered by anatomy + search)
          const allFiltered = getFilteredServices();
          // Collect unique subcategories (in display order of their first service)
          const subCatOrder = [];
          const seen = new Set();
          for (const svc of [...allFiltered].sort((a, b) => (a.displayOrder ?? 9999999) - (b.displayOrder ?? 9999999))) {
            const sub = svc.subCategory || '';
            if (!seen.has(sub)) { subCatOrder.push(sub); seen.add(sub); }
          }
          // Filter by selected subcategory, then sort: subcategory order first, display order within
          const subCatIdx = Object.fromEntries(subCatOrder.map((s, i) => [s, i]));
          const filtered = (selectedSubCategory !== null
            ? allFiltered.filter(s => (s.subCategory || '') === selectedSubCategory)
            : allFiltered
          ).slice().sort((a, b) => {
            const subA = subCatIdx[a.subCategory || ''] ?? 999;
            const subB = subCatIdx[b.subCategory || ''] ?? 999;
            if (subA !== subB) return subA - subB;
            return (a.displayOrder ?? 9999999) - (b.displayOrder ?? 9999999);
          });
          // Group by sub-category (only when showing all)
          const grouped = [];
          if (selectedSubCategory === null) {
            let lastSub = null;
            for (const svc of filtered) {
              const sub = svc.subCategory || '';
              if (sub !== lastSub) {
                grouped.push({ type: 'header', label: sub });
                lastSub = sub;
              }
              grouped.push({ type: 'service', svc });
            }
          } else {
            for (const svc of filtered) grouped.push({ type: 'service', svc });
          }
          return (
          <div className={`kiosk-screen kiosk-checkin-screen kiosk-fade-in ${selectedServices.length > 0 ? 'kiosk-has-cart' : ''}`}>
            <div className="kiosk-screen-title" style={{ fontSize: 22 }}>What would you like today?</div>

            {/* Search bar */}
            <div className="kiosk-search-bar">
              <input
                type="text"
                placeholder="Search services..."
                value={serviceSearch}
                onChange={e => setServiceSearch(e.target.value)}
                className="kiosk-search-input"
              />
              {serviceSearch && (
                <button className="kiosk-search-clear" onClick={() => setServiceSearch('')}>✕</button>
              )}
            </div>

            {/* Category grid — hidden when searching */}
            {!serviceSearch && (
              <div className="kiosk-category-grid">
                {categories.map(cat => (
                  <button key={cat.id}
                    className={`kiosk-category-tile ${selectedCategory === cat.id ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(cat.id)}>
                    {cat.name}
                  </button>
                ))}
              </div>
            )}

            {/* Sub-category nav — shown when a category is selected and has multiple subcategories */}
            {!serviceSearch && subCatOrder.filter(s => s).length > 1 && (
              <div className="kiosk-subcategory-nav">
                <button
                  className={`kiosk-subcategory-tab ${selectedSubCategory === null ? 'active' : ''}`}
                  onClick={() => setSelectedSubCategory(null)}>
                  All
                </button>
                {subCatOrder.filter(s => s).map(sub => (
                  <button key={sub}
                    className={`kiosk-subcategory-tab ${selectedSubCategory === sub ? 'active' : ''}`}
                    onClick={() => setSelectedSubCategory(sub)}>
                    {sub}
                  </button>
                ))}
              </div>
            )}

            {serviceSearch && filtered.length === 0 && (
              <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>No services found</div>
            )}

            {loadingServices ? (
              <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>Loading...</div>
            ) : (
              <div className="kiosk-service-grid kiosk-slide-up kiosk-delay-1">
                {grouped.map((item, idx) => {
                  if (item.type === 'header') {
                    if (!item.label) return null;
                    return (
                      <div key={`sub-${idx}`} className="kiosk-subcategory-header" style={{ gridColumn: '1 / -1' }}>
                        {item.label}
                      </div>
                    );
                  }
                  const svc = item.svc;
                  const isSelected = selectedServices.some(s => s.id === svc.id);
                  const descText = svc.description ? svc.description.replace(/<[^>]*>/g, '').trim() : '';
                  return (
                    <div key={svc.id} className={`kiosk-service-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleService(svc)}>
                      <div className="kiosk-service-card-top">
                        <div className="kiosk-service-card-name">{svc.name}</div>
                        <div className="kiosk-service-card-check">{isSelected ? '✓' : ''}</div>
                      </div>
                      {descText && (
                        <div className="kiosk-service-card-desc">{descText}</div>
                      )}
                      <div className="kiosk-service-card-footer">
                        <span className="kiosk-service-card-meta">{svc.duration} min</span>
                        <span className="kiosk-service-card-price">{formatPrice(svc.price)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className={`kiosk-bottom-bar ${selectedServices.length > 0 ? 'kiosk-bottom-bar-with-cart' : ''}`}>
              {selectedServices.length > 0 && (
                <div className="kiosk-cart-summary">
                  <div className="kiosk-cart-header">
                    <div className="kiosk-cart-label">{selectedServices.length} Service{selectedServices.length > 1 ? 's' : ''} Selected</div>
                    <div className="kiosk-cart-total">
                      {formatPrice(selectedServices.reduce((sum, s) => sum + s.price, 0))} · {getTotalDuration()} min
                    </div>
                  </div>
                  <div className="kiosk-cart-items">
                    {selectedServices.map(svc => (
                      <div key={svc.id} className="kiosk-cart-item" onClick={() => toggleService(svc)}>
                        <span>{svc.name}</span>
                        <span className="kiosk-cart-item-remove">✕</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="kiosk-bottom-bar-buttons">
                <button className="kiosk-nav-back" onClick={() => setStep('anatomy')}>← Back</button>
                <button className="kiosk-nav-forward" style={{ flex: 1, maxWidth: 300 }}
                  disabled={selectedServices.length === 0}
                  onClick={proceedFromServices}>
                  {selectedServices.length > 0
                    ? `Next — ${selectedServices.length} Service${selectedServices.length > 1 ? 's' : ''}`
                    : 'Select a service'}
                </button>
              </div>
            </div>
          </div>
          );
        })()}

        {/* OPTIONAL ADD-ONS — Guided Flow */}
        {step === 'addons' && (
          loadingAddOns ? (
            <div className="kiosk-screen kiosk-checkin-screen kiosk-fade-in">
              <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>Loading add-ons...</div>
            </div>
          ) : (
            <GuidedAddOnsFlow
              selectedServices={selectedServices}
              serviceAddOns={serviceAddOns}
              nailServiceIds={nailServiceIds}
              waxServiceIds={waxServiceIds}
              selectedOptionalAddOns={selectedOptionalAddOns}
              onToggleAddOn={toggleOptionalAddOn}
              onComplete={proceedFromOptionalAddOns}
              onBack={() => setStep('services')}
            />
          )
        )}

        {/* MANDATORY ADD-ONS — Do you need removal? */}
        {step === 'removal' && mandatoryServices.length > 0 && (
          <div className="kiosk-screen kiosk-fade-in">
            <GuidedRemovalDecisionScreen
              serviceName={mandatoryServices[currentMandatoryIdx]?.name}
              onDecide={(decision) => handleRemovalAnswer(mandatoryServices[currentMandatoryIdx].id, decision === 'yes')}
              onBack={() => setStep('addons')}
            />
          </div>
        )}

        {/* MANDATORY ADD-ON SELECTION — Choose removal type */}
        {step === 'removal_select' && mandatoryServices.length > 0 && (() => {
          const parentId = mandatoryServices[currentMandatoryIdx]?.id;
          const addOns = serviceAddOns[parentId]?.mandatory || [];
          const selectedAddOnIds = new Set(
            selectedMandatoryAddOns.filter(a => a.parentServiceId === parentId).map(a => a.id)
          );
          return (
            <div className="kiosk-screen kiosk-fade-in">
              <GuidedRemovalTypeScreen
                serviceName={mandatoryServices[currentMandatoryIdx]?.name}
                addOns={addOns}
                selectedAddOnIds={selectedAddOnIds}
                parentServiceId={parentId}
                onSelect={selectMandatoryAddOn}
                onBack={() => setStep('removal')}
                onContinue={advanceMandatory}
              />
            </div>
          );
        })()}

        {/* AVAILABLE — today's openings */}
        {step === 'available' && (() => {
          const uniqueTherapists = [...new Set(availableSlots.map(s => s.therapist.name))].sort();
          const filteredSlots = therapistFilter
            ? availableSlots.filter(s => s.therapist.name === therapistFilter)
            : availableSlots;
          return (
          <div className="kiosk-screen kiosk-checkin-screen kiosk-fade-in">
            <div className="kiosk-screen-title" style={{ fontSize: 22 }}>Available Now</div>
            <div className="kiosk-screen-sub" style={{ marginTop: -8 }}>Pick a time — we'll get you in today</div>

            {loadingSlots ? (
              <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>Finding available times...</div>
            ) : availableSlots.length === 0 ? (
              <div className="kiosk-walkin-identify" style={{ padding: 0 }}>
                <div className="kiosk-screen-title" style={{ fontSize: 22 }}>No openings today</div>
                <div className="kiosk-screen-sub" style={{ marginTop: -4 }}>All our therapists are booked for this service right now</div>
                <div className="kiosk-walkin-id-buttons kiosk-slide-up kiosk-delay-1" style={{ marginTop: 16 }}>
                  <button className="kiosk-walkin-id-btn" onClick={() => { setSelectedSlot(null); setTherapistFilter(null); setStep('services'); }}>
                    <div className="kiosk-walkin-id-label">Try a Shorter Service</div>
                    <div className="kiosk-walkin-id-sub">See what else is available today</div>
                  </button>
                  <button className="kiosk-walkin-id-btn" onClick={() => onBookAnotherDay?.(guest, selectedServices, selectedOptionalAddOns, selectedMandatoryAddOns)}>
                    <div className="kiosk-walkin-id-label">Book Another Day</div>
                    <div className="kiosk-walkin-id-sub">Schedule for tomorrow or later</div>
                  </button>
                </div>
              </div>
            ) : (
              <>
                {uniqueTherapists.length > 1 && (
                  <select
                    className="kiosk-signup-input"
                    style={{ maxWidth: 300, margin: '0 auto 12px', textAlign: 'center' }}
                    value={therapistFilter || ''}
                    onChange={e => { setTherapistFilter(e.target.value || null); setSelectedSlot(null); }}
                  >
                    <option value="">All Therapists</option>
                    {uniqueTherapists.map(name => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                )}
                <div style={{ width: '100%' }} className="kiosk-slide-up kiosk-delay-1">
                  {filteredSlots.map((slot, i) => {
                    const isSelected = selectedSlot === slot;
                    return (
                      <div key={i} className={`kiosk-addon-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => setSelectedSlot(slot)}>
                        <div className="kiosk-addon-info">
                          <div className="kiosk-addon-name">{slot.time}</div>
                          <div className="kiosk-addon-meta">with {slot.therapist.name}{slot.therapist.title ? ` · ${slot.therapist.title}` : ''}</div>
                        </div>
                        <div className="kiosk-addon-check">{isSelected ? '✓' : ''}</div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <div className="kiosk-bottom-bar">
              <button className="kiosk-nav-back" onClick={() => { setSelectedSlot(null); setTherapistFilter(null); setStep('services'); }}>← Back</button>
              <button className="kiosk-nav-forward" style={{ flex: 1, maxWidth: 300 }}
                disabled={!selectedSlot}
                onClick={() => { kev('available', 'slot_selected', { time: selectedSlot?.time, therapist: selectedSlot?.therapist?.name }); setStep('confirm'); }}>
                {selectedSlot ? `Book ${selectedSlot.time} with ${selectedSlot.therapist.name}` : 'Select a time'}
              </button>
            </div>
          </div>
          );
        })()}

        {/* CONFIRM — summary before booking */}
        {step === 'confirm' && selectedSlot && (
          <div className="kiosk-screen kiosk-checkin-screen kiosk-fade-in">
            <div className="kiosk-screen-title" style={{ fontSize: 22 }}>Confirm Your Booking</div>

            <div className="kiosk-invoice-card kiosk-slide-up kiosk-delay-1">
              <div className="kiosk-invoice-row">
                <div>
                  <div className="kiosk-svc-name">Today at {selectedSlot.time}</div>
                  <div className="kiosk-svc-detail">with {selectedSlot.therapist.name}</div>
                </div>
              </div>
              <div className="kiosk-divider" />
              {selectedServices.map((svc, i) => (
                <React.Fragment key={svc.id}>
                  <div className="kiosk-invoice-row">
                    <div>
                      <div className="kiosk-svc-name">{svc.name}</div>
                      <div className="kiosk-svc-detail">{svc.duration} min</div>
                    </div>
                    <div className="kiosk-svc-price">{formatPrice(svc.price)}</div>
                  </div>
                  {/* Show add-ons for this service */}
                  {selectedOptionalAddOns.filter(a => a.parentServiceId === svc.id).map(addon => (
                    <div key={addon.id} className="kiosk-invoice-row" style={{ paddingLeft: 16 }}>
                      <div>
                        <div className="kiosk-svc-detail" style={{ color: '#777' }}>+ {addon.name}</div>
                      </div>
                      <div className="kiosk-svc-price" style={{ fontSize: 13 }}>{formatPrice(addon.price)}</div>
                    </div>
                  ))}
                  {selectedMandatoryAddOns.filter(a => a.parentServiceId === svc.id).map(addon => (
                    <div key={addon.id} className="kiosk-invoice-row" style={{ paddingLeft: 16 }}>
                      <div>
                        <div className="kiosk-svc-detail" style={{ color: '#777' }}>+ {addon.name}</div>
                      </div>
                      <div className="kiosk-svc-price" style={{ fontSize: 13 }}>{formatPrice(addon.price)}</div>
                    </div>
                  ))}
                  {i < selectedServices.length - 1 && <div className="kiosk-divider" />}
                </React.Fragment>
              ))}
              <div className="kiosk-divider" />
              <div className="kiosk-invoice-row">
                <div className="kiosk-total-label">Total · {getTotalDuration()} min</div>
                <div className="kiosk-total-price">{formatPrice(getTotalPrice())}</div>
              </div>
            </div>

            {error && <div style={{ color: '#c44', fontSize: 13, textAlign: 'center', marginTop: 8 }}>{error}</div>}
            <div className="kiosk-bottom-bar">
              <button className="kiosk-nav-back" onClick={() => { setError(null); setStep('available'); }}>← Back</button>
              <button className="kiosk-nav-forward" style={{ flex: 1, maxWidth: 300 }}
                disabled={loading}
                onClick={confirmBooking}>
                {loading ? 'Booking...' : 'Confirm Booking'}
              </button>
            </div>
          </div>
        )}

        {/* SUCCESS */}
        {step === 'success' && (() => {
          return (
          <div className="kiosk-screen kiosk-success-screen kiosk-fade-in">
            <div className="kiosk-fireworks">
              {[...Array(60)].map((_, i) => {
                const angle = (i / 60) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
                const dist = 120 + Math.random() * 300;
                const isHeart = i % 3 === 0;
                return (
                  <div key={i} className={`kiosk-firework-particle${isHeart ? ' heart' : ''}`} style={{
                    left: '50%',
                    top: '45%',
                    '--x': `${Math.cos(angle) * dist}px`,
                    '--y': `${Math.sin(angle) * dist}px`,
                    '--color': ['#FFB6C1','#F9A8D4','#DDA0DD','#B5EAD7','#FFDAC1','#FF69B4','#E8A0BF','#C3B1E1'][i % 8],
                    '--delay': `${Math.random() * 0.4}s`,
                    '--size': `${isHeart ? Math.random() * 10 + 8 : Math.random() * 7 + 5}px`,
                    '--spin': `${Math.random() > 0.5 ? 1 : -1}`,
                  }} />
                );
              })}
            </div>
            <div className="kiosk-success-icon">✓</div>
            <div className="kiosk-success-title">
              {checkedIn ? "You're in!" : "You're booked!"}
            </div>
            <div className="kiosk-success-sub">
              {selectedSlot
                ? <>Today at <strong>{selectedSlot.time}</strong> with <strong>{selectedSlot.therapist.name}</strong></>
                : 'Your appointment is confirmed.'}
            </div>
            <div className="kiosk-success-sub" style={{ marginTop: 4, color: '#bbb' }}>
              Please take a seat — we'll call you when we're ready.
            </div>
            {bookedAppointmentGroupId && !checkedIn && (
              <button className="kiosk-btn-primary" style={{ marginTop: 16 }}
                disabled={loading}
                onClick={checkInNow}>
                {loading ? 'Checking in...' : 'Check In Now'}
              </button>
            )}
            {error && <div style={{ color: '#c44', fontSize: 13, textAlign: 'center', marginTop: 8 }}>{error}</div>}
            <div className="kiosk-done-reset">This screen resets automatically</div>
            <button className="kiosk-btn-ghost" style={{ marginTop: 16 }} onClick={onBack}>Done</button>
          </div>
          );
        })()}

      </div>
      {showWarning && <IdleWarningModal countdown={countdown} onDismiss={dismissWarning} onReset={onBack} />}
    </div>
  );
}
